import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { CustomerForm } from './customer';
import { InvoiceForm } from './invoice';

import dynamicsBcLogo from '@public/img/dynamicsbc-logo.png';

import type { DynamicsBcSettingsModuleProps } from '@src/pages/settings/types';
import { ProductForm } from './products';

export const DynamicsForm: React.FC<DynamicsBcSettingsModuleProps> = ({
  dynamicsBcUserDefaultData,
  setDynamicsBcUserDefaultData,
  handleSelectChange,
  currentMonthPaymentTermsError,
  setCurrentMonthPaymentTermsError,
}) => {
  return (
    <>
      <Row className="mt-4 mb-4">
        <Col>
          <div className="settins-heading">
            <img src={dynamicsBcLogo} alt="Dynamics BC logo" width="75" loading="lazy" />
            <h2>Dynamics Business Central Settings</h2>
          </div>
          <h4>Customer</h4>
          <p>Set how customers are created and updated in your accounts. Customer data is received with the order transfer.</p>
          <p className="text-muted">
            Customer, general business, and VAT business posting groups are assigned by Customer/API Templates in Business Central when a customer is created.
          </p>
          <CustomerForm
            dynamicsBcUserDefaultData={dynamicsBcUserDefaultData}
            setDynamicsBcUserDefaultData={setDynamicsBcUserDefaultData}
            handleSelectChange={handleSelectChange}
          />
        </Col>
      </Row>

      <hr />

      <Row className="mt-4 mb-4">
        <Col>
          <h4>Invoice</h4>
          <p>Set how invoices should be created in your accounts.</p>
          <Form.Group as={Row} className="mb-3" controlId="invoice-pdf-enabled">
            <Form.Label column sm={6}>
              Transfer invoice PDF
            </Form.Label>
            <Col sm={6}>
              <Form.Check
                type="switch"
                id="invoice-pdf-enabled"
                label="Enable"
                aria-describedby="invoice-pdf-description"
                checked={dynamicsBcUserDefaultData.invoice_pdf_enabled === true}
                onChange={(event) => setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, invoice_pdf_enabled: event.target.checked })}
              />
              <Form.Text id="invoice-pdf-description" className="d-block mt-2" muted>
                Attach the original Rentman invoice PDF when a new invoice is transferred to Business Central.
              </Form.Text>
            </Col>
          </Form.Group>
          <InvoiceForm
            dynamicsBcUserDefaultData={dynamicsBcUserDefaultData}
            setDynamicsBcUserDefaultData={setDynamicsBcUserDefaultData}
            handleSelectChange={handleSelectChange}
            currentMonthPaymentTermsError={currentMonthPaymentTermsError}
            setCurrentMonthPaymentTermsError={setCurrentMonthPaymentTermsError}
          />
        </Col>
      </Row>

      <hr />

      <Row className="mt-4 mb-4">
        <Col>
          <h4>Payments</h4>
          <Form.Group as={Row} className="mb-3" controlId="payment-sync-enabled">
            <Form.Label column sm={6}>
              Enable payment synchronization
            </Form.Label>
            <Col sm={6}>
              <Form.Check
                type="switch"
                id="payment-sync-enabled"
                label="Enable"
                aria-describedby="payment-sync-description"
                checked={dynamicsBcUserDefaultData.payment_sync_enabled === true}
                onChange={(event) => setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, payment_sync_enabled: event.target.checked })}
              />
              <Form.Text id="payment-sync-description" className="d-block mt-2" muted>
                Automatically transfer new payments applied to invoices in Business Central to Rentman, including partial payments. Requires an active General
                ledger entries webhook. Earlier payments are not imported.
              </Form.Text>
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <hr />

      <Row>
        <Col className="mt-4 mb-4">
          <h4>Products</h4>
          <p>Set how products are created and updated in Dynamics Business Central.</p>
          <ProductForm
            dynamicsBcUserDefaultData={dynamicsBcUserDefaultData}
            setDynamicsBcUserDefaultData={setDynamicsBcUserDefaultData}
            handleSelectChange={handleSelectChange}
          />
        </Col>
      </Row>
    </>
  );
};
